import React, { useEffect, useState } from 'react';
import { Badge, Button } from '../ui';
import { Icon } from '../../icons';
import type { AuthHeadersFn } from './phoneSensorApi';
import { agoText, mmwaveView, presenceLabel } from './mmwave';
import { getMmwave, identifyMmwave, mintMmwaveToken, setMmwavePlacement, type MmwaveZone } from './mmwaveApi';

interface Props {
  sensorId: number;
  status: string;
  metadata?: Record<string, unknown>;
  getAuthHeaders: AuthHeadersFn;
}

const ZONE_REFRESH_MS = 30_000;

/**
 * Live section of an mmWave sensor's detail panel. Values come from props, which the
 * App's `mmwave_state` handler keeps current; only the covering-zone list is fetched.
 */
export function MmwaveSection({ sensorId, status, metadata, getAuthHeaders }: Props) {
  const v = mmwaveView(status, metadata);
  const [now, setNow] = useState(() => Date.now());
  const [zones, setZones] = useState<MmwaveZone[] | null>(null);
  const [hasToken, setHasToken] = useState<boolean | null>(null);
  const [secrets, setSecrets] = useState<{ token: string; state_url: string } | null>(null);
  const [identifyNote, setIdentifyNote] = useState('');
  const [bearing, setBearing] = useState(v.bearingDeg !== null ? String(v.bearingDeg) : '');
  const [range, setRange] = useState(String(v.rangeM));
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => getMmwave(sensorId, getAuthHeaders)
      .then(d => { if (alive) { setZones(d.zones); setHasToken(d.has_token); } })
      .catch(() => { if (alive) setError('Could not load this sensor.'); });
    load();
    const t = setInterval(load, ZONE_REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, [sensorId, getAuthHeaders]);

  const mint = () => mintMmwaveToken(sensorId, getAuthHeaders)
    .then(s => { setSecrets(s); setHasToken(true); })
    .catch(() => setError('Could not generate a token.'));

  const identify = () => identifyMmwave(sensorId, getAuthHeaders)
    .then(() => setIdentifyNote('Queued — the LED blinks within 30 s.'))
    .catch(() => setError('Identify failed.'));

  const saveFacing = () => {
    const b = Number(bearing), r = Number(range);
    if (!Number.isFinite(b) || b < 0 || b >= 360 || !Number.isFinite(r) || r < 1 || r > 6) {
      setError('Bearing 0–359°, range 1–6 m.');
      return;
    }
    setError('');
    setMmwavePlacement(sensorId, b, r, getAuthHeaders).catch(() => setError('Could not save the facing.'));
  };

  const presenceTone = v.presence === null ? 'neutral' : v.presence ? 'warning' : 'accent';

  return (
    <div className="mmwave-section">
      <div className="detail-row">
        <label>Link</label>
        <span>
          <Badge tone={v.online ? 'accent' : 'neutral'} dot>{v.online ? 'Online' : 'Offline'}</Badge>
          <span className="mmwave-muted"> last seen {agoText(v.lastSeen, now)}</span>
        </span>
      </div>
      <div className="detail-row">
        <label>Presence</label>
        <span>
          <Badge tone={presenceTone} dot>
            {v.presence && <Icon name="occupied" size={12} />} {presenceLabel(v.presence)}
          </Badge>
          {v.presence !== null && v.presenceSince && (
            <span className="mmwave-muted"> since {v.presenceSince.toLocaleTimeString()}</span>
          )}
        </span>
      </div>
      <div className="detail-row"><label>Targets</label><span className="num">{v.targets ?? '—'}</span></div>
      <div className="detail-row">
        <label>Distance</label>
        <span className="num">{v.distanceCm !== null ? `${v.distanceCm.toFixed(0)} cm` : '—'}</span>
      </div>

      <div className="detail-row">
        <label>Zones</label>
        <span>
          {zones === null ? '…' : zones.length === 0
            ? <span className="mmwave-muted">None — add this sensor to a zone’s covering sensors</span>
            : zones.map(z => <span key={z.id} className="mmwave-zone">{z.name} <span className="mmwave-muted">({z.status})</span></span>)}
        </span>
      </div>

      <div className="mmwave-facing">
        <label htmlFor={`mmwave-bearing-${sensorId}`}>Bearing °</label>
        <input id={`mmwave-bearing-${sensorId}`} className="num" type="number" min={0} max={359}
          value={bearing} onChange={e => setBearing(e.target.value)} />
        <label htmlFor={`mmwave-range-${sensorId}`}>Range m</label>
        <input id={`mmwave-range-${sensorId}`} className="num" type="number" min={1} max={6} step={0.5}
          value={range} onChange={e => setRange(e.target.value)} />
        <Button size="sm" onClick={saveFacing}>Save facing</Button>
      </div>

      <div className="mmwave-actions">
        <Button size="sm" onClick={identify} disabled={!v.online}>Identify</Button>
        <Button size="sm" variant="ghost" onClick={mint}>
          {hasToken ? 'Rotate device token' : 'Generate device token'}
        </Button>
      </div>
      {identifyNote && <p className="mmwave-muted">{identifyNote}</p>}

      {secrets && (
        <div className="mmwave-secrets">
          <p className="mmwave-muted">
            Shown once. Paste into <code>scripts/mmwave/secrets.yaml</code>, then flash
            (<code>./esphome.sh upload mr60bha2.yaml --device &lt;ip&gt;</code>). Rotating revokes the old token.
          </p>
          <pre data-testid="mmwave-secrets">{`overwatch_state_url: "${secrets.state_url}"\noverwatch_sensor_token: "${secrets.token}"`}</pre>
        </div>
      )}
      {error && <p className="mmwave-error" role="alert">{error}</p>}
    </div>
  );
}
